// CVBankPicker — picks candidates from the HR user's existing CV bank
// (uploads across all their past jobs, deduped by email) and attaches the
// selected CVs to the current job.
//
// Used inside the resume-upload modal alongside the file-upload tab so HR
// can choose between "upload fresh" and "pick from bank".

import React, { useEffect, useMemo, useState } from "react";
import { Search, Loader2, CheckCircle, FileText } from "lucide-react";
import api from "../../../services/api";
import { toast } from "react-toastify";

export default function CVBankPicker({ jobId, onAttached }) {
  const [q, setQ]               = useState("");
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [picked, setPicked]     = useState(() => new Set());
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/resumes/bank", { params: { q, limit: 100 } });
        if (!cancelled) setItems(r.data?.data || []);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load CV bank");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250); // debounce
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const togglePick = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAttach = async () => {
    const resumeIds = Array.from(picked);
    if (!resumeIds.length) return;
    if (!jobId) return toast.error("Job not loaded yet");
    try {
      setAttaching(true);
      const r = await api.post("/resumes/bank/attach", { jobId, resumeIds });
      toast.success(`Attached ${r.data?.count || resumeIds.length} CVs from bank`);
      setPicked(new Set());
      onAttached?.(r.data?.count || resumeIds.length);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to attach CVs");
    } finally {
      setAttaching(false);
    }
  };

  const count = picked.size;
  const empty = !loading && items.length === 0;

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search bank by name, email, or filename"
          className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
        />
      </div>

      <div className="border border-slate-200 rounded-lg max-h-80 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center p-8 text-slate-500 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {empty && (
          <div className="p-8 text-center text-slate-500 text-sm">
            No CVs in your bank yet. Upload a few resumes and they'll show up here for future jobs.
          </div>
        )}
        {!loading && items.map((r) => {
          const id    = String(r._id);
          const name  = r.candidateName || r.analysis?.basicInfo?.name || r.fileName || "Unknown";
          const email = r.email || r.analysis?.basicInfo?.email || "—";
          const isPicked = picked.has(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => togglePick(id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-100 last:border-b-0 transition ${
                isPicked ? "bg-orange-50" : "hover:bg-slate-50"
              }`}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                isPicked ? "bg-orange-500 border-orange-500" : "border-slate-300"
              }`}>
                {isPicked && <CheckCircle className="w-4 h-4 text-white" />}
              </div>
              <FileText className="w-4 h-4 text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{name}</div>
                <div className="text-xs text-slate-500 truncate">{email} · {r.fileName}</div>
              </div>
              {r.analysis?.scores?.overallScore != null && (
                <div className="text-xs font-semibold text-slate-600">
                  {Math.round(r.analysis.scores.overallScore)}%
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {count > 0 ? `${count} selected` : "Pick CVs to attach to this job"}
        </div>
        <button
          onClick={handleAttach}
          disabled={!count || attaching}
          className="px-4 py-2 rounded-md bg-orange-500 text-white font-medium hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {attaching && <Loader2 className="w-4 h-4 animate-spin" />}
          Attach {count > 0 ? `(${count})` : ""}
        </button>
      </div>
    </div>
  );
}
